import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const MotionLink = motion.create(Link);

const EnhancedButton = ({
  children,
  onClick,
  variant = "primary",
  size = "md",
  disabled = false,
  className = "",
  icon,
  href,
  target,
  rel,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center relative overflow-hidden font-bold rounded-lg transition-all duration-300 ease-in-out transform focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95 select-none";

  const variants = {
    primary:
      "bg-brand-gold text-black hover:bg-brand-gold/90 hover:shadow-glow focus:ring-brand-gold",
    secondary:
      "border-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-black focus:ring-brand-gold",
    outline:
      "border-2 border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-black focus:ring-brand-gold",
    ghost:
      "text-gray-800 dark:text-brand-cream hover:bg-gray-100 dark:hover:bg-white/10 focus:ring-gray-300 dark:focus:ring-white/20",
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg",
    xl: "px-10 py-5 text-xl",
  };

  const buttonClasses = `${baseClasses} ${variants[variant]} ${
    sizes[size]
  } ${className} ${
    disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
  }`;

  const buttonVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const shimmerVariants = {
    initial: { x: "-100%" },
    hover: {
      x: "100%",
      transition: { duration: 0.6, ease: "easeInOut" },
    },
  };

  const content = (
    <>
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
        variants={shimmerVariants}
        initial="initial"
        whileHover="hover"
      />
      <span className="relative z-10 flex items-center justify-center gap-2">
        {icon && <span className="text-current">{icon}</span>}
        {children}
      </span>
    </>
  );

  const motionProps = {
    variants: buttonVariants,
    initial: "initial",
    whileHover: disabled ? {} : "hover",
    whileTap: disabled ? {} : "tap",
    className: buttonClasses,
    ...props,
  };

  if (href) {
    const isInternal = href.startsWith("/") && !target;

    if (isInternal) {
      if (disabled) {
        return (
          <span
            className={buttonClasses}
            aria-disabled="true"
            role="link"
            {...props}
          >
            {content}
          </span>
        );
      }

      return (
        <MotionLink to={href} {...motionProps}>
          {content}
        </MotionLink>
      );
    }

    if (disabled) {
      return (
        <span className={buttonClasses} aria-disabled="true" {...props}>
          {content}
        </span>
      );
    }

    return (
      <motion.a href={href} target={target} rel={rel} {...motionProps}>
        {content}
      </motion.a>
    );
  }

  return (
    <motion.button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      {...motionProps}
    >
      {content}
    </motion.button>
  );
};

export default EnhancedButton;
